import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Trade, Client, Employee } from '../../types';
import { dailyBrokerageContribution } from '../../utils/brokerage';
import { istDay } from '../../utils/formatters';

interface CommissionCalculatorProps {
  open: boolean;
  onClose: () => void;
  trades: Trade[];
  clients: Client[];
  employees: Employee[];
}

const C = {
  primary: '#4F46E5',
  positive: '#059669',
  warning: '#D97706',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  surface: '#FFFFFF',
};

const inr = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);

// Date helpers (same as other pages)
type DateMode = 'day' | 'week' | 'month' | 'year' | 'all';
function startOfDay(d: Date) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function addDays(d: Date, n: number) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
function isSameDay(a: Date, b: Date) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function weekStart(d: Date) { return addDays(startOfDay(d), -d.getDay()); }
function weekEnd(d: Date) { return addDays(weekStart(d), 6); }
function monthStart(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function monthEnd(d: Date) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function yearStart(d: Date) { return new Date(d.getFullYear(), 0, 1); }
function yearEnd(d: Date) { return new Date(d.getFullYear(), 11, 31); }
function getDateRange(mode: DateMode, sel: Date): { start: Date; end: Date } | null {
  if (mode === 'all') return null;
  if (mode === 'day') { const s = startOfDay(sel); return { start: s, end: new Date(s.getTime() + 86400000 - 1) }; }
  if (mode === 'week') return { start: weekStart(sel), end: weekEnd(sel) };
  if (mode === 'month') return { start: monthStart(sel), end: monthEnd(sel) };
  return { start: yearStart(sel), end: yearEnd(sel) };
}
function dateRangeLabel(mode: DateMode, sel: Date): string {
  if (mode === 'all') return 'All Time';
  if (mode === 'day') return sel.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const fmt = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  if (mode === 'week') return `${fmt(weekStart(sel))} – ${fmt(weekEnd(sel))}`;
  if (mode === 'month') return sel.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  return `${sel.getFullYear()}`;
}
function bumpDate(mode: DateMode, sel: Date, dir: -1 | 1): Date {
  if (mode === 'day') return addDays(sel, dir);
  if (mode === 'week') return addDays(sel, 7 * dir);
  if (mode === 'month') return new Date(sel.getFullYear(), sel.getMonth() + dir, 1);
  if (mode === 'year') return new Date(sel.getFullYear() + dir, 0, 1);
  return sel;
}
function isNextDisabled(mode: DateMode, sel: Date): boolean {
  if (mode === 'all') return true;
  const rng = getDateRange(mode, sel);
  return rng ? rng.end >= new Date() : true;
}

// (tradeBucketDate removed — Bajaj-style daily attribution evaluates buyDay
// and sellDay independently for each trade; see utils/brokerage.ts.)

const headerSx = {
  fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase' as const,
  letterSpacing: '0.06em', color: C.textSecondary, py: 1.25,
  borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' as const, bgcolor: '#F8FAFC',
};
const numSx = {
  fontWeight: 500, fontFeatureSettings: '"tnum"', fontVariantNumeric: 'tabular-nums' as const,
  fontSize: '0.85rem', color: C.textPrimary,
};

export default function CommissionCalculator({ open, onClose, trades, clients, employees }: CommissionCalculatorProps) {
  const [dateMode, setDateMode] = useState<DateMode>('day');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Filter trades by period — include any trade whose buy day OR sell day
  // falls in the period (Bajaj-style: a single trade may contribute to two days).
  const filteredTrades = useMemo(() => {
    const rng = getDateRange(dateMode, selectedDate);
    const base = trades.filter((t) => !t.isDeleted);
    if (!rng) return base;
    const startStr = istDay(rng.start.toISOString());
    const endStr = istDay(rng.end.toISOString());
    return base.filter((t) => {
      const buyDay = istDay(t.date || t.createdAt || '');
      const sellDay = (t.saleLots || 0) > 0 ? istDay(t.saleDate || t.date || t.createdAt || '') : null;
      return (buyDay >= startStr && buyDay <= endStr) ||
             (!!sellDay && sellDay >= startStr && sellDay <= endStr);
    });
  }, [trades, dateMode, selectedDate]);

  const employeeStats = useMemo(() => {
    const clientMap = new Map(clients.map((c) => [c.clientId, c]));
    const rng = getDateRange(dateMode, selectedDate);
    const startStr = rng ? istDay(rng.start.toISOString()) : '0000-00-00';
    const endStr = rng ? istDay(rng.end.toISOString()) : '9999-99-99';
    const empClientMap = new Map<string, string[]>();
    clients.forEach((c) => {
      if (c.referredByEmployeeId) {
        const existing = empClientMap.get(c.referredByEmployeeId) || [];
        existing.push(c.clientId);
        empClientMap.set(c.referredByEmployeeId, existing);
      }
    });

    return employees.map((emp) => {
      const referredClientIds = empClientMap.get(emp.employeeId) || [];
      const referredClients = referredClientIds.map((cid) => clientMap.get(cid)).filter(Boolean);
      const empTrades = filteredTrades.filter((t) => referredClientIds.includes(t.clientId));
      const totalTrades = empTrades.length;
      const totalTurnover = empTrades.reduce((sum, t) => {
        const buyVal = t.purchaseLots * t.lotSize * t.purchasePrice;
        const sellVal = (t.saleLots || 0) * t.lotSize * (t.salePrice || 0);
        return sum + buyVal + sellVal;
      }, 0);
      // Bajaj-style daily attribution: sum buy-side commission whose buyDay
      // is in period + sell-side commission whose sellDay is in period.
      const commissionEarned = empTrades.reduce((sum, t) => {
        const client = clientMap.get(t.clientId);
        if (!client) return sum;
        const c = dailyBrokerageContribution(t, client.brokerageRates as any);
        let v = 0;
        if (c.buyDay >= startStr && c.buyDay <= endStr) v += c.buy;
        if (c.sellDay && c.sellDay >= startStr && c.sellDay <= endStr) v += c.sell;
        return sum + v;
      }, 0);
      return {
        employeeId: emp.employeeId, name: emp.name, designation: emp.designation,
        clientCount: referredClients.length, totalTrades, totalTurnover, commissionEarned, isActive: emp.isActive,
      };
    });
  }, [filteredTrades, clients, employees, dateMode, selectedDate]);

  const sorted = useMemo(() => [...employeeStats].sort((a, b) => b.commissionEarned - a.commissionEarned), [employeeStats]);
  const grandTotal = sorted.reduce((s, e) => s + e.commissionEarned, 0);
  const grandTurnover = sorted.reduce((s, e) => s + e.totalTurnover, 0);
  const grandTrades = sorted.reduce((s, e) => s + e.totalTrades, 0);
  const maxCommission = Math.max(...sorted.map((e) => e.commissionEarned), 1);

  const segmentedModes: { key: DateMode; label: string }[] = [
    { key: 'day', label: 'Day' }, { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' }, { key: 'year', label: 'Year' }, { key: 'all', label: 'All' },
  ];

  const kpis: { label: string; value: string; icon: React.ReactNode; accent: string; accentBg: string }[] = [
    { label: 'Commission', value: inr(grandTotal), icon: <TrendingUpIcon sx={{ fontSize: 18 }} />, accent: C.positive, accentBg: '#D1FAE5' },
    { label: 'Turnover', value: inr(grandTurnover), icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18 }} />, accent: C.warning, accentBg: '#FEF3C7' },
    { label: 'Trades', value: grandTrades.toString(), icon: <GroupsOutlinedIcon sx={{ fontSize: 18 }} />, accent: C.primary, accentBg: '#EEF2FF' },
    { label: 'Dealers', value: sorted.filter((e) => e.clientCount > 0).length.toString(), icon: <PeopleOutlineIcon sx={{ fontSize: 18 }} />, accent: '#2563EB', accentBg: '#DBEAFE' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
        <Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Reports</Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: C.textPrimary, letterSpacing: '-0.02em' }}>Employee Commission</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: C.textSecondary, border: `1px solid ${C.border}`, borderRadius: 1, '&:hover': { bgcolor: '#F8FAFC' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Date toolbar */}
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, borderBottom: `1px solid ${C.border}`, bgcolor: '#FAFBFC' }}>
          <Box sx={{ display: 'inline-flex', p: 0.375, bgcolor: '#F1F5F9', borderRadius: 1.5, gap: 0.25 }}>
            {segmentedModes.map((m) => {
              const active = dateMode === m.key;
              return (
                <Box key={m.key} onClick={() => { setDateMode(m.key); if (m.key !== 'day') setSelectedDate(new Date()); }}
                  sx={{
                    px: 1.25, py: 0.5, borderRadius: 1, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    color: active ? C.textPrimary : C.textSecondary,
                    bgcolor: active ? C.surface : 'transparent',
                    boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
                    transition: 'all 0.15s ease', '&:hover': { color: C.textPrimary },
                  }}>
                  {m.label}
                </Box>
              );
            })}
          </Box>
          {dateMode !== 'all' && (
            <>
              <Box sx={{ width: '1px', height: 22, bgcolor: C.border }} />
              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton size="small" onClick={() => setSelectedDate((d) => bumpDate(dateMode, d, -1))}
                  sx={{ width: 28, height: 28, border: `1px solid ${C.border}`, borderRadius: 1, color: C.textSecondary, '&:hover': { bgcolor: '#F8FAFC' } }}>
                  <ChevronLeftIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Box sx={{ px: 1.25, py: 0.5, minWidth: 170, textAlign: 'center', bgcolor: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: 1, fontSize: '0.78rem', fontWeight: 600, color: C.textPrimary, fontFeatureSettings: '"tnum"' }}>
                  {dateRangeLabel(dateMode, selectedDate)}
                </Box>
                <IconButton size="small" onClick={() => setSelectedDate((d) => bumpDate(dateMode, d, 1))} disabled={isNextDisabled(dateMode, selectedDate)}
                  sx={{ width: 28, height: 28, border: `1px solid ${C.border}`, borderRadius: 1, color: C.textSecondary, '&:hover': { bgcolor: '#F8FAFC' }, '&.Mui-disabled': { opacity: 0.4 } }}>
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </IconButton>
                {!isSameDay(selectedDate, new Date()) && (
                  <Button size="small" onClick={() => setSelectedDate(new Date())}
                    sx={{ ml: 0.5, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: C.primary, px: 1, minWidth: 'auto', '&:hover': { bgcolor: '#EEF2FF' } }}>
                    Today
                  </Button>
                )}
              </Box>
            </>
          )}
          {dateMode === 'all' && <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: C.textSecondary }}>All-time data</Typography>}
        </Box>

        {/* KPIs */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Grid container spacing={1.5}>
            {kpis.map((k) => (
              <Grid item xs={6} md={3} key={k.label}>
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: C.surface, border: `1px solid ${C.border}` }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Typography sx={{ fontWeight: 600, color: C.textSecondary, letterSpacing: '0.04em', fontSize: '0.62rem', textTransform: 'uppercase' }}>{k.label}</Typography>
                    <Box sx={{ width: 26, height: 26, borderRadius: 1.25, bgcolor: k.accentBg, color: k.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{k.icon}</Box>
                  </Box>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: C.textPrimary, letterSpacing: '-0.02em', fontFeatureSettings: '"tnum"', lineHeight: 1.2 }}>{k.value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Bar chart */}
        {sorted.filter((e) => e.commissionEarned > 0).length > 0 && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#FAFBFC', border: `1px solid ${C.border}` }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.textPrimary, mb: 1 }}>Performance Overview</Typography>
              {sorted.filter((e) => e.commissionEarned > 0).map((emp) => (
                <Box key={emp.employeeId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ width: 90, fontSize: '0.72rem', fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.name}</Typography>
                  <Box sx={{ flex: 1, height: 10, bgcolor: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
                    <Box sx={{ width: `${(emp.commissionEarned / maxCommission) * 100}%`, height: '100%', background: `linear-gradient(90deg, ${C.primary}, ${C.positive})`, borderRadius: 999, transition: 'width 0.4s ease' }} />
                  </Box>
                  <Typography sx={{ width: 80, fontSize: '0.72rem', fontWeight: 700, textAlign: 'right', color: C.positive, fontFeatureSettings: '"tnum"' }}>{inr(emp.commissionEarned)}</Typography>
                </Box>
              ))}
            </Paper>
          </Box>
        )}

        {/* Table */}
        <Paper elevation={0} sx={{ mx: 2, mb: 2, borderRadius: 1.5, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Employee', 'Designation', 'Clients', 'Trades', 'Turnover', 'Commission', 'Share'].map((h, i) => (
                    <TableCell key={h} align={i >= 2 && h !== 'Employee' && h !== 'Designation' ? 'right' : 'left'} sx={headerSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: '0.85rem', color: C.textSecondary }}>No employees found</Typography>
                    </TableCell>
                  </TableRow>
                ) : sorted.map((emp) => {
                  const share = grandTotal > 0 ? (emp.commissionEarned / grandTotal) * 100 : 0;
                  return (
                    <TableRow key={emp.employeeId} hover sx={{ '&:hover': { bgcolor: '#F8FAFC' }, '& td': { borderBottom: `1px solid ${C.border}`, py: 1.25 } }}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: C.textPrimary }}>{emp.name}</Typography>
                          {!emp.isActive && <Chip label="Inactive" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#F1F5F9', color: C.textSecondary }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', color: C.textSecondary }}>{emp.designation || '—'}</TableCell>
                      <TableCell align="right" sx={numSx}>{emp.clientCount}</TableCell>
                      <TableCell align="right" sx={numSx}>{emp.totalTrades}</TableCell>
                      <TableCell align="right" sx={{ ...numSx, color: '#2563EB' }}>{inr(emp.totalTurnover)}</TableCell>
                      <TableCell align="right" sx={{ ...numSx, fontWeight: 700, color: emp.commissionEarned > 0 ? C.positive : C.textSecondary }}>{inr(emp.commissionEarned)}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                          <Box sx={{ width: 50, height: 5, bgcolor: '#F1F5F9', borderRadius: 3, overflow: 'hidden' }}>
                            <Box sx={{ width: `${share}%`, height: '100%', bgcolor: C.primary, borderRadius: 3 }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.textPrimary, minWidth: 36, textAlign: 'right', fontFeatureSettings: '"tnum"' }}>{share.toFixed(1)}%</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {sorted.length > 0 && (
                  <TableRow sx={{ bgcolor: '#F8FAFC', '& td': { fontWeight: 700, borderTop: `1px solid ${C.borderStrong}`, py: 1.5 } }}>
                    <TableCell colSpan={2} sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.textPrimary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total · {sorted.filter(e => e.clientCount > 0).length} dealers</TableCell>
                    <TableCell align="right" sx={{ ...numSx, fontWeight: 700 }}>{sorted.reduce((s, e) => s + e.clientCount, 0)}</TableCell>
                    <TableCell align="right" sx={{ ...numSx, fontWeight: 700 }}>{grandTrades}</TableCell>
                    <TableCell align="right" sx={{ ...numSx, fontWeight: 700, color: '#2563EB' }}>{inr(grandTurnover)}</TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.35, borderRadius: 1, bgcolor: C.primary, color: '#fff', fontSize: '0.88rem', fontWeight: 800, fontFeatureSettings: '"tnum"' }}>{inr(grandTotal)}</Box>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5, borderTop: `1px solid ${C.border}` }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: C.textSecondary }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
